import { supabase } from '@/lib/supabase';
import type {
  Location,
  LocationHours,
  LocationHourOverride,
  LocationStatus,
} from '@/lib/types';

/**
 * Locations API helpers
 *
 * Every mutation goes through a named DB function (Solution_Design §1.4).
 * This module wraps each RPC + the SELECTs RLS allows directly.
 *
 * Naming convention:
 *   - listLocations / getLocation                 - direct SELECT (RLS scoped)
 *   - createLocation / updateLocation             - DB functions
 *   - transitionLocation                          - DB function (status change)
 *   - listLocationHours / upsertWeeklyHours       - hours pattern
 *   - listLocationOverrides / upsertOverride / deleteOverride
 */

// ----- locations ------------------------------------------------------------

export async function listLocations(): Promise<Location[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .order('status', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Location[];
}

export async function getLocation(id: string): Promise<Location | null> {
  const { data, error } = await supabase
    .from('locations')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data as Location | null;
}

export async function createLocation(input: {
  name: string;
  address?: string | null;
  phone?: string | null;
  timezone?: string | null;
  shift_split_time?: string | null;
}): Promise<Location> {
  const { data, error } = await supabase.rpc('location_create', {
    p_name: input.name,
    p_address: input.address ?? null,
    p_phone: input.phone ?? null,
    p_timezone: input.timezone ?? null,
    p_shift_split_time: input.shift_split_time ?? '12:00:00',
  });
  if (error) throw error;
  return data as Location;
}

export async function updateLocation(
  id: string,
  patch: {
    name?: string;
    address?: string | null;
    phone?: string | null;
    timezone?: string | null;
    shift_split_time?: string | null;
  }
): Promise<Location> {
  const { data, error } = await supabase.rpc('location_update', {
    p_id: id,
    p_name: patch.name ?? null,
    p_address: patch.address ?? null,
    p_phone: patch.phone ?? null,
    p_timezone: patch.timezone ?? null,
    p_shift_split_time: patch.shift_split_time ?? null,
  });
  if (error) throw error;
  return data as Location;
}

export async function transitionLocation(
  id: string,
  newStatus: LocationStatus,
  reason: string
): Promise<Location> {
  const { data, error } = await supabase.rpc('location_transition', {
    p_id: id,
    p_new_status: newStatus,
    p_reason: reason,
  });
  if (error) throw error;
  return data as Location;
}

// ----- publish lead-days (0082) --------------------------------------------

/**
 * Owner-only: set the per-pharmacy publishing lead-days rule. Manager is
 * read-only on this — they see the derived deadline countdown but cannot edit.
 */
export async function setLocationPublishLead(
  locationId: string,
  leadDays: number
): Promise<Location> {
  const { data, error } = await supabase.rpc('location_publish_lead_set', {
    p_location_id: locationId,
    p_lead_days: leadDays,
  });
  if (error) throw error;
  return data as Location;
}

// ----- hours (weekly) -------------------------------------------------------

export async function listLocationHours(
  locationId: string
): Promise<LocationHours[]> {
  const { data, error } = await supabase
    .from('location_hours')
    .select('*')
    .eq('location_id', locationId)
    .order('day_of_week', { ascending: true });
  if (error) throw error;
  return (data ?? []) as LocationHours[];
}

/**
 * Replace the weekly pattern. Pass an array of {day_of_week, is_closed,
 * open_time?, close_time?} - one row per day you want to upsert.
 */
export async function upsertWeeklyHours(
  locationId: string,
  hours: Array<{
    day_of_week: number;
    is_closed: boolean;
    open_time: string | null;
    close_time: string | null;
  }>
): Promise<LocationHours[]> {
  const { data, error } = await supabase.rpc('location_hours_upsert_week', {
    p_location_id: locationId,
    p_hours: hours,
  });
  if (error) throw error;
  return (data ?? []) as LocationHours[];
}

// ----- hour overrides -------------------------------------------------------

export async function listLocationOverrides(
  locationId: string
): Promise<LocationHourOverride[]> {
  const { data, error } = await supabase
    .from('location_hour_overrides')
    .select('*')
    .eq('location_id', locationId)
    .order('override_date', { ascending: true });
  if (error) throw error;
  return (data ?? []) as LocationHourOverride[];
}

export async function upsertHourOverride(
  locationId: string,
  override: {
    override_date: string;
    is_closed: boolean;
    open_time: string | null;
    close_time: string | null;
    reason: string;
  }
): Promise<LocationHourOverride> {
  const { data, error } = await supabase.rpc('location_hour_override_upsert', {
    p_location_id: locationId,
    p_override_date: override.override_date,
    p_is_closed: override.is_closed,
    p_open_time: override.open_time,
    p_close_time: override.close_time,
    p_reason: override.reason,
  });
  if (error) throw error;
  return data as LocationHourOverride;
}

export async function deleteHourOverride(
  locationId: string,
  overrideDate: string
): Promise<void> {
  const { error } = await supabase.rpc('location_hour_override_delete', {
    p_location_id: locationId,
    p_override_date: overrideDate,
  });
  if (error) throw error;
}
